import { mapValues, memoize } from '@exodus/basic-utils';
import { createSelector } from 'reselect';
import { WalletAccount } from '@exodus/models';
import { MY_STATE } from '@exodus/redux-dependency-injection';
import { updateAccount, mergeAccountsData, setAccounts, setAsset, setLoaded, setLoading, } from './common.js';
function toFirstUpper(value) {
    const first = value[0];
    if (!first)
        return '';
    return first.toUpperCase() + value.slice(1);
}
const getKeyFromAssetSource = ({ walletAccount, assetName, network }) => network ? `${walletAccount}.${assetName}.${network}` : `${walletAccount}.${assetName}`;
export default function createStateHelperAndSelectors({ slice, createInitialPerAssetData, assetSourceDataSelectors = [], }) {
    if (!slice)
        throw new Error('expected string "slice" - the top level state key');
    const initialPerAssetData = createInitialPerAssetData ?? (() => undefined);
    const createPerAccountState = () => ({
        error: null,
        loaded: false,
        data: {},
    });
    const resetAccount = (state, walletAccount) => {
        if (!walletAccount)
            throw new Error('expected walletAccount');
        return {
            ...state,
            [walletAccount]: createPerAccountState(),
        };
    };
    const resetAsset = (state, walletAccount, asset) => setAsset(state, walletAccount, asset.name, initialPerAssetData({ state, asset }));
    const getAccount = (state, walletAccount) => {
        if (!walletAccount)
            throw new Error('expected walletAccount');
        return state[walletAccount];
    };
    const getAssets = (state, walletAccount) => {
        if (!walletAccount)
            throw new Error('expected walletAccount');
        return state[walletAccount]?.data;
    };
    const getAsset = (state, walletAccount, asset) => {
        if (!walletAccount)
            throw new Error('expected walletAccount');
        if (!asset) {
            console.warn('multi-account-redux: expected asset, returning null for backward compatibility, please investigate');
            return null;
        }
        const stored = getAssets(state, walletAccount)?.[asset.name];
        return stored ?? initialPerAssetData({ state, asset: asset });
    };
    const setLoadingError = (state, walletAccount, error) => {
        const reset = resetAccount(state, walletAccount);
        return updateAccount(reset, walletAccount, { error, loading: false });
    };
    const createInitialState = () => ({
        [WalletAccount.DEFAULT_NAME]: createPerAccountState(),
    });
    const createAccountAssetsSelectorDefinition = {
        id: 'createAccountAssetsSelector',
        selectorFactory: (myStateSelector) => memoize((walletAccount) => createSelector(myStateSelector, (myState) => getAssets(myState, walletAccount))),
        dependencies: [
            { selector: MY_STATE },
        ],
    };
    const getAccountAssetsSelectorDefinition = {
        id: 'getAccountAssetsSelector',
        resultFunction: (myState) => (walletAccount) => getAssets(myState, walletAccount),
        dependencies: [
            { selector: MY_STATE },
        ],
    };
    const activeAccountAssetsSelectorDefinition = {
        id: 'activeAccountAssetsSelector',
        resultFunction: (myState, activeAccount) => getAssets(myState, activeAccount),
        dependencies: [
            { selector: MY_STATE },
            { module: 'walletAccounts', selector: 'active' },
        ],
    };
    const createAssetSourceSelectorDefinition = {
        id: 'createAssetSourceSelector',
        selectorFactory: (myStateSelector, getAssetInstanceSelector) => memoize(({ walletAccount, assetName }) => createSelector(myStateSelector, getAssetInstanceSelector, (myState, getAssetInstance) => getAsset(myState, walletAccount, getAssetInstance(assetName))), getKeyFromAssetSource),
        dependencies: [
            { selector: MY_STATE },
            { module: 'assets', selector: 'getAsset' },
        ],
    };
    const createAssetSourceSelectorOldDefinition = {
        id: 'createAssetSourceSelectorOld',
        resultFunction: (myState, activeWalletAccount, getAssetInstance) => memoize(({ walletAccount = activeWalletAccount, assetName }) => getAsset(myState, walletAccount, getAssetInstance(assetName)), getKeyFromAssetSource),
        dependencies: [
            { selector: MY_STATE },
            { module: 'walletAccounts', selector: 'active' },
            { module: 'assets', selector: 'getAsset' },
        ],
    };
    const createBaseAssetSourceSelectorOldDefinition = {
        id: 'createBaseAssetSourceSelectorOld',
        resultFunction: (myState, activeWalletAccount, getAssetInstance) => memoize(({ walletAccount, assetName }) => getAsset(myState, walletAccount, getAssetInstance(assetName).baseAsset), getKeyFromAssetSource),
        dependencies: [
            { selector: MY_STATE },
            { module: 'walletAccounts', selector: 'active' },
            { module: 'assets', selector: 'getAsset' },
        ],
    };
    const createBaseAssetSourceSelectorDefinition = {
        id: 'createBaseAssetSourceSelector',
        selectorFactory: (myStateSelector, getAssetInstanceSelector) => memoize(({ walletAccount, assetName }) => createSelector(myStateSelector, getAssetInstanceSelector, (myState, getAssetInstance) => getAsset(myState, walletAccount, getAssetInstance(assetName).baseAsset)), getKeyFromAssetSource),
        dependencies: [
            { selector: MY_STATE },
            { module: 'assets', selector: 'getAsset' },
        ],
    };
    const createActiveAssetSourceSelectorOldDefinition = {
        id: 'createActiveAssetSourceSelectorOld',
        resultFunction: (activeWalletAccount, getAssetInstance, myState) => memoize((assetName) => getAsset(myState, activeWalletAccount, getAssetInstance(assetName))),
        dependencies: [
            { module: 'walletAccounts', selector: 'active' },
            { module: 'assets', selector: 'getAsset' },
            { selector: MY_STATE },
        ],
    };
    const createActiveAssetSourceSelectorDefinition = {
        id: 'createActiveAssetSourceSelector',
        selectorFactory: (activeWalletAccountSelector, getAssetInstanceSelector, myStateSelector) => memoize((assetName) => createSelector(activeWalletAccountSelector, getAssetInstanceSelector, myStateSelector, (activeWalletAccount, getAssetInstance, myState) => getAsset(myState, activeWalletAccount, getAssetInstance(assetName)))),
        dependencies: [
            { module: 'walletAccounts', selector: 'active' },
            { module: 'assets', selector: 'getAsset' },
            { selector: MY_STATE },
        ],
    };
    const createActiveBaseAssetSourceSelectorDefinition = {
        id: 'createActiveBaseAssetSourceSelector',
        selectorFactory: (activeWalletAccountSelector, getAssetInstanceSelector, myStateSelector) => memoize((assetName) => createSelector(activeWalletAccountSelector, getAssetInstanceSelector, myStateSelector, (activeWalletAccount, getAssetInstance, myState) => getAsset(myState, activeWalletAccount, getAssetInstance(assetName).baseAsset))),
        dependencies: [
            { module: 'walletAccounts', selector: 'active' },
            { module: 'assets', selector: 'getAsset' },
            { selector: MY_STATE },
        ],
    };
    const createIsWalletAccountLoadingSelectorOldDefinition = {
        id: 'createIsWalletAccountLoadingSelectorOld',
        selectorFactory: (activeWalletAccountSelector, myStateSelector) => createSelector(activeWalletAccountSelector, myStateSelector, (activeWalletAccount, subState) => (walletAccount = activeWalletAccount) => Boolean(subState[walletAccount]?.loading)),
        dependencies: [
            { module: 'walletAccounts', selector: 'active' },
            { selector: MY_STATE },
        ],
    };
    const createIsWalletAccountLoadingSelectorDefinition = {
        id: 'createIsWalletAccountLoadingSelector',
        selectorFactory: (myStateSelector) => memoize((walletAccount) => createSelector(myStateSelector, (subState) => Boolean(getAccount(subState, walletAccount)?.loading))),
        dependencies: [{ selector: MY_STATE }],
    };
    const isActiveWalletAccountLoadingDefinition = {
        id: 'isActiveWalletAccountLoading',
        resultFunction: (activeWalletAccount, subState) => Boolean(subState[activeWalletAccount]?.loading),
        dependencies: [
            { module: 'walletAccounts', selector: 'active' },
            { selector: MY_STATE },
        ],
    };
    const createIsWalletAccountLoadedSelectorOldDefinition = {
        id: 'createIsWalletAccountLoadedSelectorOld',
        resultFunction: (activeWalletAccount, subState) => (walletAccount = activeWalletAccount) => Boolean(subState[walletAccount]?.loaded),
        dependencies: [
            { module: 'walletAccounts', selector: 'active' },
            { selector: MY_STATE },
        ],
    };
    const createIsWalletAccountLoadedSelectorDefinition = {
        id: 'createIsWalletAccountLoadedSelector',
        selectorFactory: (myStateSelector) => memoize((walletAccount) => createSelector(myStateSelector, (subState) => Boolean(getAccount(subState, walletAccount)?.loaded))),
        dependencies: [{ selector: MY_STATE }],
    };
    const isActiveWalletAccountLoadedDefinition = {
        id: 'isActiveWalletAccountLoaded',
        resultFunction: (activeWalletAccount, subState) => Boolean(subState[activeWalletAccount]?.loaded),
        dependencies: [
            { module: 'walletAccounts', selector: 'active' },
            { selector: MY_STATE },
        ],
    };
    const withAssetSourceDataSelectors = assetSourceDataSelectors.flatMap(({ name, selector }) => {
        const accountMapper = (accountAssets) => mapValues(accountAssets ?? {}, (assetState) => selector(assetState));
        return [
            {
                id: `createAccountAssets${toFirstUpper(name)}Selector`,
                selectorFactory: (createAccountAssetsSelector) => memoize((walletAccount) => createSelector(createAccountAssetsSelector(walletAccount), accountMapper)),
                dependencies: [
                    { selector: 'createAccountAssetsSelector' },
                ],
            },
            {
                id: `activeAccountAssets${toFirstUpper(name)}Selector`,
                resultFunction: accountMapper,
                dependencies: [
                    { selector: 'activeAccountAssetsSelector' },
                ],
            },
            {
                id: `getAccountAssets${toFirstUpper(name)}Selector`,
                resultFunction: (getAccountAssets) => memoize((walletAccount) => accountMapper(getAccountAssets(walletAccount))),
                dependencies: [
                    { selector: 'getAccountAssetsSelector' },
                ],
            },
            {
                id: `createAssetSource${toFirstUpper(name)}Selector`,
                selectorFactory: (createAssetSourceSelector) => memoize((assetSource) => createSelector(createAssetSourceSelector(assetSource), (sourceData) => selector(sourceData)), getKeyFromAssetSource),
                dependencies: [
                    { selector: 'createAssetSourceSelector' },
                ],
            },
            {
                id: `createActiveAssetSource${toFirstUpper(name)}Selector`,
                selectorFactory: (createActiveAssetSourceSelector) => memoize((assetName) => createSelector(createActiveAssetSourceSelector(assetName), (sourceData) => selector(sourceData))),
                dependencies: [
                    { selector: 'createActiveAssetSourceSelector' },
                ],
            },
            {
                id: `getAssetSource${toFirstUpper(name)}Selector`,
                resultFunction: (getAssetSource, activeWalletAccount) => memoize(({ assetName, walletAccount = activeWalletAccount }) => selector(getAssetSource({ assetName, walletAccount })), getKeyFromAssetSource),
                dependencies: [
                    { selector: 'createAssetSourceSelectorOld' },
                    { module: 'walletAccounts', selector: 'active' },
                ],
            },
        ];
    });
    return {
        createInitialState,
        getAccount,
        updateAccount,
        mergeAccountsData,
        setAccounts,
        resetAccount,
        setLoaded,
        setLoading,
        setLoadingError,
        getAssets,
        getAsset,
        setAsset,
        resetAsset,
        selectorDefinitions: [
            createAssetSourceSelectorDefinition,
            createAssetSourceSelectorOldDefinition,
            createBaseAssetSourceSelectorDefinition,
            createBaseAssetSourceSelectorOldDefinition,
            createActiveAssetSourceSelectorOldDefinition,
            createActiveAssetSourceSelectorDefinition,
            createActiveBaseAssetSourceSelectorDefinition,
            createIsWalletAccountLoadingSelectorOldDefinition,
            createIsWalletAccountLoadingSelectorDefinition,
            createIsWalletAccountLoadedSelectorOldDefinition,
            createIsWalletAccountLoadedSelectorDefinition,
            isActiveWalletAccountLoadingDefinition,
            isActiveWalletAccountLoadedDefinition,
            createAccountAssetsSelectorDefinition,
            activeAccountAssetsSelectorDefinition,
            getAccountAssetsSelectorDefinition,
            ...withAssetSourceDataSelectors,
        ],
    };
}
